import React from 'react';
import { osc, wobble, drawOn, stagger, loop, clamp, lerp, easeOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — shapes and time-telling act themselves
 * out for a 5-year-old audience. Pure function of `t`; no state or wall-clock.
 * See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ─── shared palette ─────────────────────────────────────────────────────── */
const CAPTION_PILL = 'rgba(255,255,255,0.90)';
const FONT = 'Inter,Nunito,sans-serif';

/* ━━━ ShapesScene helpers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** Cute face overlay (eyes + smile) drawn on any shape centre. */
const Face = (cx: number, cy: number, ey: number, size: number) => {
  const eo = size * 0.18;
  return (
    <g>
      {/* eyes */}
      <circle cx={cx - eo} cy={cy + ey} r={size * 0.09} fill="#1f2937" />
      <circle cx={cx + eo} cy={cy + ey} r={size * 0.09} fill="#1f2937" />
      {/* shine dots */}
      <circle cx={cx - eo + size * 0.04} cy={cy + ey - size * 0.04} r={size * 0.03} fill="#fff" />
      <circle cx={cx + eo + size * 0.04} cy={cy + ey - size * 0.04} r={size * 0.03} fill="#fff" />
      {/* smile */}
      <path
        d={`M${cx - eo},${cy + ey + size * 0.13} Q${cx},${cy + ey + size * 0.26} ${cx + eo},${cy + ey + size * 0.13}`}
        fill="none" stroke="#1f2937" strokeWidth={size * 0.05} strokeLinecap="round"
      />
    </g>
  );
};

/**
 * A single bouncing, labelled shape: circle | square | triangle | star.
 * `reveal` is 0→1 draw-on progress; `bounce` is the current y offset.
 */
const ShapeCard = (
  kind: 0 | 1 | 2 | 3,
  cx: number,
  cy: number,
  reveal: number,
  bounce: number,
  accent: string,
) => {
  const R = 48;
  const y = cy + bounce;
  const scale = easeOut(clamp(reveal * 1.4, 0, 1));      // pop in fast, hold
  const op = clamp(reveal * 2, 0, 1);
  const labels = ['Circle', 'Square', 'Triangle', 'Star'];

  let shape: React.ReactNode;
  if (kind === 0) {
    // Circle
    shape = (
      <g>
        <circle cx={cx} cy={y} r={R} fill={accent} stroke="#fff" strokeWidth={4} />
        {Face(cx, y, -6, R)}
      </g>
    );
  } else if (kind === 1) {
    // Square (rounded)
    const s = R * 1.55;
    shape = (
      <g>
        <rect x={cx - s / 2} y={y - s / 2} width={s} height={s} rx={14} fill={accent} stroke="#fff" strokeWidth={4} />
        {Face(cx, y, -4, s * 0.5)}
      </g>
    );
  } else if (kind === 2) {
    // Triangle
    const h = R * 1.72;
    const pts = `${cx},${y - h * 0.62} ${cx - h * 0.72},${y + h * 0.38} ${cx + h * 0.72},${y + h * 0.38}`;
    shape = (
      <g>
        <polygon points={pts} fill={accent} stroke="#fff" strokeWidth={4} strokeLinejoin="round" />
        {Face(cx, y + 6, -4, R * 0.8)}
      </g>
    );
  } else {
    // Star (5-point)
    const outerR = R * 1.1, innerR = R * 0.45;
    const pts = Array.from({ length: 10 }, (_, k) => {
      const r = k % 2 === 0 ? outerR : innerR;
      const a = (k / 10) * Math.PI * 2 - Math.PI / 2;
      return `${cx + r * Math.cos(a)},${y + r * Math.sin(a)}`;
    }).join(' ');
    shape = (
      <g>
        <polygon points={pts} fill={accent} stroke="#fff" strokeWidth={3} strokeLinejoin="round" />
        {Face(cx, y, -4, R * 0.72)}
      </g>
    );
  }

  // Guard: when scale is ~0 (not yet revealed) just place at origin of the card.
  const safeScale = scale < 0.001 ? 0.001 : scale;
  return (
    <g key={kind} opacity={op} transform={`translate(${cx} ${y}) scale(${safeScale}) translate(${-cx} ${-y})`}>
      {shape}
      {/* name label */}
      <rect
        x={cx - 42} y={y + R + 12} width={84} height={26} rx={13}
        fill="rgba(255,255,255,0.92)" stroke={accent} strokeWidth={2}
        opacity={clamp(reveal * 3 - 1, 0, 1)}
      />
      <text
        x={cx} y={y + R + 30} textAnchor="middle"
        fill="#1e293b" fontSize={14} fontWeight={800} fontFamily={FONT}
        opacity={clamp(reveal * 3 - 1, 0, 1)}
      >
        {labels[kind]}
      </text>
    </g>
  );
};

/* ━━━ ShapesScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ShapesSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started — drives all animation. */
  t?: number;
}

/**
 * Four friendly cartoon shapes (circle, square, triangle, star) pop in one
 * at a time, bounce, and dance together. Each has cute eyes and a smile.
 * Loop ~8 s.
 */
export const ShapesScene: React.FC<ShapesSceneProps> = ({
  opacity = 1,
  accentColor = '#f472b6',
  label = "Let's learn our shapes!",
  t = 0,
}) => {
  /* ── layout ─────────────────────────────────────────────────────────────── */
  const CYCLE = 8;
  const phase = loop(t, CYCLE);                    // 0→1 in 8 s, loops
  const tLocal = phase * CYCLE;                    // 0..8 s within each cycle

  const positions: [number, number][] = [
    [130, 230],   // circle
    [310, 230],   // square
    [490, 230],   // triangle
    [670, 230],   // star
  ];

  // Accent palette per shape; accentColor tints the first shape.
  const accents = [accentColor, '#60a5fa', '#4ade80', '#fbbf24'];

  /*
   * Each shape draws-on at t = i*1.2 s, reveal duration 0.8 s.
   * After t = 5 s all four bounce together.
   */
  const reveals = [0, 1, 2, 3].map(i => drawOn(tLocal, 0.8, stagger(i, 1.2)));

  /*
   * Bouncing: individual staggered bob while appearing; group bounce after all in.
   * allIn is 1 once all four are fully revealed (t > ~4.8 s).
   */
  const allIn = clamp((tLocal - 5.0) / 0.4, 0, 1);
  const groupBounce = wobble(t, 0.55, 16) * allIn;

  const bounces = [0, 1, 2, 3].map(i => {
    const solo = wobble(t, 0.7, 10, stagger(i, 0.4)) * clamp(reveals[i] - 0.1, 0, 1) * (1 - allIn);
    return solo + groupBounce;
  });

  /* pastel gradient sky background */
  const bgTop = osc(t, 9, 0, 1) > 0.5 ? '#fce7f3' : '#ede9fe';
  const bgBot = '#fff7ed';

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="ss-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bgTop} />
          <stop offset="100%" stopColor={bgBot} />
        </linearGradient>
      </defs>

      <rect x={0} y={0} width={800} height={450} fill="url(#ss-bg)" />

      {/* Drifting sparkle dots in the background */}
      {Array.from({ length: 8 }, (_, k) => {
        const bx = 80 + k * 90;
        const by = 60 + wobble(t, 3.2 + k * 0.3, 18, (k / 8) * Math.PI * 2);
        const br = 4 + k % 3;
        return (
          <circle
            key={k} cx={bx} cy={by} r={br}
            fill={accents[k % 4]}
            opacity={osc(t, 2.1 + k * 0.2, 0.15, 0.55, (k / 8) * Math.PI * 2)}
          />
        );
      })}

      {/* Ground strip */}
      <rect x={0} y={370} width={800} height={80} rx={0} fill="#bbf7d0" opacity={0.6} />

      {/* The four shape cards */}
      {([0, 1, 2, 3] as Array<0 | 1 | 2 | 3>).map(i =>
        ShapeCard(i, positions[i][0], positions[i][1], reveals[i], bounces[i], accents[i]),
      )}

      {/* Caption pill */}
      <g transform="translate(400 415)">
        <rect x={-240} y={-22} width={480} height={40} rx={20} fill={CAPTION_PILL} stroke={accentColor} strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#1e293b" fontSize={16} fontWeight={700} fontFamily={FONT}>
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ TellingTimeScene helpers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** Draw a clock face centred at (cx,cy) radius R, with given hour/minute angles. */
const ClockFace = (
  cx: number,
  cy: number,
  R: number,
  hourAngleDeg: number,
  minuteAngleDeg: number,
  accent: string,
) => {
  // Hour ticks
  const ticks = Array.from({ length: 12 }, (_, k) => {
    const a = (k / 12) * Math.PI * 2 - Math.PI / 2;
    const major = k % 3 === 0;
    const r1 = major ? R - 14 : R - 9;
    return (
      <line
        key={k}
        x1={cx + R * Math.cos(a)} y1={cy + R * Math.sin(a)}
        x2={cx + r1 * Math.cos(a)} y2={cy + r1 * Math.sin(a)}
        stroke="#475569" strokeWidth={major ? 4 : 2} strokeLinecap="round"
      />
    );
  });

  // Hour numbers (3, 6, 9, 12)
  const nums = [12, 3, 6, 9].map((n, k) => {
    const a = (k / 4) * Math.PI * 2 - Math.PI / 2;
    return (
      <text
        key={n}
        x={cx + (R - 28) * Math.cos(a)} y={cy + (R - 28) * Math.sin(a) + 5}
        textAnchor="middle" fill="#1e293b" fontSize={18} fontWeight={800} fontFamily={FONT}
      >
        {n}
      </text>
    );
  });

  const hRad = (hourAngleDeg - 90) * (Math.PI / 180);
  const mRad = (minuteAngleDeg - 90) * (Math.PI / 180);

  return (
    <g>
      {/* Shadow */}
      <ellipse cx={cx + 4} cy={cy + R + 6} rx={R * 0.85} ry={10} fill="rgba(0,0,0,0.12)" />
      {/* Face */}
      <circle cx={cx} cy={cy} r={R} fill="#fff" stroke={accent} strokeWidth={7} />
      <circle cx={cx} cy={cy} r={R - 2} fill="none" stroke="#e2e8f0" strokeWidth={2} />
      {ticks}
      {nums}
      {/* Minute hand */}
      <line
        x1={cx} y1={cy}
        x2={cx + (R - 20) * Math.cos(mRad)} y2={cy + (R - 20) * Math.sin(mRad)}
        stroke="#334155" strokeWidth={5} strokeLinecap="round"
      />
      {/* Hour hand */}
      <line
        x1={cx} y1={cy}
        x2={cx + (R - 36) * Math.cos(hRad)} y2={cy + (R - 36) * Math.sin(hRad)}
        stroke="#1e40af" strokeWidth={8} strokeLinecap="round"
      />
      {/* Centre pin */}
      <circle cx={cx} cy={cy} r={8} fill={accent} stroke="#fff" strokeWidth={3} />
    </g>
  );
};

/** A tiny house outline (always anchored at bottom). */
const House = (x: number, y: number, windowLit: boolean) => (
  <g>
    {/* walls */}
    <rect x={x} y={y - 52} width={64} height={52} rx={4} fill="#e2e8f0" stroke="#94a3b8" strokeWidth={2} />
    {/* roof */}
    <polygon points={`${x - 8},${y - 52} ${x + 32},${y - 90} ${x + 72},${y - 52}`} fill="#f97316" stroke="#ea580c" strokeWidth={2} strokeLinejoin="round" />
    {/* door */}
    <rect x={x + 22} y={y - 28} width={20} height={28} rx={4} fill="#92400e" />
    {/* window */}
    <rect x={x + 8} y={y - 44} width={18} height={18} rx={3} fill={windowLit ? '#fef08a' : '#bae6fd'} stroke="#94a3b8" strokeWidth={2} />
    <line x1={x + 17} y1={y - 44} x2={x + 17} y2={y - 26} stroke="#94a3b8" strokeWidth={1} />
    <line x1={x + 8} y1={y - 35} x2={x + 26} y2={y - 35} stroke="#94a3b8" strokeWidth={1} />
  </g>
);

/* ━━━ TellingTimeScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface TellingTimeSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started — drives all animation. */
  t?: number;
}

/**
 * A friendly clock sweeps through morning (7 o'clock, sun rising), noon
 * (12 o'clock, high sun), and night (8 o'clock, moon + stars). The sky,
 * house window, and clock label change with the time.  Loop ~9 s.
 */
export const TellingTimeScene: React.FC<TellingTimeSceneProps> = ({
  opacity = 1,
  accentColor = '#f59e0b',
  label = 'Learning to tell the time!',
  t = 0,
}) => {
  /* ── time phases ──────────────────────────────────────────────────────────
   * Phase 0 = 0..3 s  → morning  (7:00)
   * Phase 1 = 3..6 s  → noon     (12:00)
   * Phase 2 = 6..9 s  → night    (8:00)
   * t is continuous; we derive a 0..1 progress within the 9s cycle.
   */
  const CYCLE = 9;
  const phase = loop(t, CYCLE);               // 0→1, loops every 9 s
  const tLocal = phase * CYCLE;              // 0..9 s

  // Which phase are we in (0, 1, 2) and how far through it (0→1)?
  const phaseIdx = Math.floor(tLocal / 3) % 3;   // 0, 1, or 2
  const phaseP = (tLocal % 3) / 3;                // 0→1 within the phase

  /* Clock hands sweep smoothly to the target hour */
  type HourSpec = { hour: number; minutes: number; label: string };
  const specs: HourSpec[] = [
    { hour: 7,  minutes: 0,  label: '7 o\'clock' },
    { hour: 12, minutes: 0,  label: '12 o\'clock' },
    { hour: 8,  minutes: 0,  label: '8 o\'clock' },
  ];

  const cur = specs[phaseIdx];
  const next = specs[(phaseIdx + 1) % 3];

  // Sweep the hands during the last 0.8 s of each phase.
  const sweepP = clamp((phaseP - 0.7) / 0.3, 0, 1);

  const hourAngle  = lerp(cur.hour * 30,  next.hour * 30,  sweepP);   // degrees (0=12)
  const minAngle   = lerp(cur.minutes * 6, next.minutes * 6, sweepP);

  /*
   * Sky colours per phase.
   * Morning: warm orange → yellow   Noon: bright blue   Night: dark navy
   */
  const skies = [
    { top: '#fed7aa', bot: '#fef9c3' },  // morning
    { top: '#7dd3fc', bot: '#bfdbfe' },  // noon
    { top: '#1e3a5f', bot: '#0f172a' },  // night
  ];
  const skyTop = skies[phaseIdx].top;
  const skyBot = skies[phaseIdx].bot;

  /* Sun / Moon position */
  const isNight = phaseIdx === 2;
  // Morning: sun rises from horizon (left arc). Noon: near top. Night: moon top-right.
  const sunAngles = [210, 270, 60];       // degrees round a circle, 0=right
  const sunAngleDeg = lerp(sunAngles[phaseIdx], sunAngles[(phaseIdx + 1) % 3], sweepP);
  const sunRad = (sunAngleDeg * Math.PI) / 180;
  const orbitR = 170;
  const sunCX = 400 + orbitR * Math.cos(sunRad);
  const sunCY = 200 + orbitR * Math.sin(sunRad);

  // Clock wobble (gentle bob)
  const clockBob = wobble(t, 2.2, 5);

  // House window lit at night
  const windowLit = phaseIdx === 2;

  // Stars (only at night)
  const starPositions = [
    { x: 80,  y: 55  },
    { x: 170, y: 80  },
    { x: 600, y: 40  },
    { x: 700, y: 70  },
    { x: 740, y: 120 },
    { x: 55,  y: 120 },
    { x: 310, y: 55  },
  ];
  const nightAlpha = phaseIdx === 2 ? clamp(phaseP * 4, 0, 1) : clamp((1 - phaseP) * 4, 0, 1) * (phaseIdx === 1 ? 0 : 1);

  /* current time label with fade */
  const timeLabel = cur.label;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="tt-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={skyTop} />
          <stop offset="100%" stopColor={skyBot} />
        </linearGradient>
        <radialGradient id="tt-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fffbeb" />
          <stop offset="60%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <radialGradient id="tt-moon" cx="38%" cy="38%">
          <stop offset="0%" stopColor="#fef9c3" />
          <stop offset="100%" stopColor="#d1d5db" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={360} fill="url(#tt-sky)" />
      {/* Ground */}
      <rect x={0} y={360} width={800} height={90} fill={isNight ? '#0f2e14' : '#86efac'} />
      <rect x={0} y={356} width={800} height={8} rx={4} fill={isNight ? '#14532d' : '#4ade80'} opacity={0.8} />

      {/* Stars (night only) */}
      {starPositions.map((s, k) => (
        <g key={k} opacity={nightAlpha * osc(t, 1.4 + k * 0.2, 0.5, 1.0, (k / 7) * Math.PI * 2)}>
          <polygon
            points={Array.from({ length: 10 }, (_, j) => {
              const r2 = j % 2 === 0 ? 8 : 3.5;
              const a2 = (j / 10) * Math.PI * 2 - Math.PI / 2;
              return `${s.x + r2 * Math.cos(a2)},${s.y + r2 * Math.sin(a2)}`;
            }).join(' ')}
            fill="#fef9c3"
          />
        </g>
      ))}

      {/* Sun or Moon */}
      {!isNight ? (
        /* Sun with rays */
        <g transform={`translate(${sunCX} ${sunCY})`}>
          <g transform={`rotate(${(t * 12) % 360})`} opacity={0.85}>
            {Array.from({ length: 12 }, (_, k) => {
              const a = (k / 12) * Math.PI * 2;
              return (
                <line key={k}
                  x1={34 * Math.cos(a)} y1={34 * Math.sin(a)}
                  x2={48 * Math.cos(a)} y2={48 * Math.sin(a)}
                  stroke="#fcd34d" strokeWidth={3} strokeLinecap="round"
                />
              );
            })}
          </g>
          <circle r={26} fill="url(#tt-sun)" />
        </g>
      ) : (
        /* Moon */
        <g transform={`translate(${sunCX} ${sunCY})`} opacity={nightAlpha}>
          <circle r={28} fill="url(#tt-moon)" />
          {/* crescent shadow */}
          <circle cx={10} cy={-4} r={22} fill={skyTop} opacity={0.85} />
        </g>
      )}

      {/* House */}
      {House(60, 380, windowLit)}
      {House(680, 380, windowLit)}

      {/* Big friendly clock */}
      <g transform={`translate(0 ${clockBob})`}>
        {ClockFace(400, 215, 110, hourAngle, minAngle, accentColor)}
      </g>

      {/* Time label badge on the clock */}
      <g transform={`translate(400 ${340 + clockBob})`}>
        <rect x={-72} y={-20} width={144} height={36} rx={18} fill={accentColor} />
        <text x={0} y={5} textAnchor="middle" fill="#fff" fontSize={17} fontWeight={800} fontFamily={FONT}>
          {timeLabel}
        </text>
      </g>

      {/* Caption pill */}
      <g transform="translate(400 422)">
        <rect x={-248} y={-22} width={496} height={40} rx={20} fill={CAPTION_PILL} stroke={accentColor} strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#1e293b" fontSize={16} fontWeight={700} fontFamily={FONT}>
          {label}
        </text>
      </g>
    </g>
  );
};
